import {ApiId} from '../distribution-account/distribution-account.model';
import {TtdRateType} from '../target/target.service';

export enum SegmentState {
  ACTIVE = 'ACTIVE',
  DELETED = 'DELETED'
}

export interface HasChildren<T> {
  children: T[]
}

export interface SegmentTaxonomy extends HasChildren<SegmentTaxonomy> {
  id: number;
  name: string;
  children: SegmentTaxonomy[];
  hasChildren: boolean;
  level?: number;
  entityType: 'TAXONOMY' | 'SEGMENT'
}

interface SegmentReach {
  merged: number;
  xpredict: number;
  narthaPure: number;
  narthaConverted: number;
}

interface SegmentPlatformReach {
  pubmatic: number,
  ttd: number,
  yoc: number,
  activeAgentEmetriq: number,
  activeAgentBcn: number,
  activeAgentIqd: number,
  activeAgentNetpoint: number,
  activeAgentQuarterMedia: number,
  activeAgentRnd: number,
  activeAgentTargetVideo: number,
  adex: number,
  adform: number,
  adobe: number,
  xandr: number,
  googleDbmEmetriqAdvertiser: number,
  googleDbm: number,
  googleDdp: number,
  googleDfp: number,
}

export interface Segment extends SegmentTaxonomy {
  id: number;
  name: string;
  description: string;
  partnerId: number;
  clientSideId: string;
  reach: SegmentReach;
  platformReach?: SegmentPlatformReach;
  sharedAccounts: number[];
  mainType: string | null;
  subType: string | null;
  audienceType: string | null;
  category: string;
  freeText: string;
  keywords: string | null;
  state: SegmentState;
  hasChildren: boolean;
  children: Segment[];
  parent?: number;
  parentId?: number;
  taxonomyId?: number;
  platformAttributes?: PlatformShareAttributes[];
}

export interface PlatformShareAttributes {
  foreignId: string,
  integrationId?: string,
  platformAttributes: SegmentPlatformAttributesDTO,
  distributionAccount: SegmentMappingDistributionAccountDTO
}

export interface SegmentMappingDistributionAccountDTO {
  targetSystemName: string;
  description: string;
  apiId: ApiId;
  foreignAccountId: string;
}

export interface SegmentPlatformAttributesDTO {
  ttdPricing: TheTradeDeskPricingDTO[],
  activeAgentAttributes: ActiveAgentAttributesDTO[],
  googleAttributes: GoogleAttributesDTO[],
  ttdCompliance?: string
}

export interface ActiveAgentAttributesDTO {
  activeAgentSegmentId: number,
  attributeId: number,
  attributeKeySegmentSubKey: string,
  attributeName: string,
  cpm?: number
}

export interface GoogleAttributesDTO {
  clientId?: string,
  currencyCode: string,
  cost: number
}

export interface TheTradeDeskPricingDTO {
  partnerId: string,
  partnerName?: string,
  cpm?: number,
  percentOfMediaCostRate?: number,
  type: TtdRateType,
  lastUpdated: string
}

export interface TheTradeDeskCategoryPricingDTO {
  categoryName: string,
  partnerId: string,
  partnerName?: string,
  cpm?: number,
  percentOfMediaCostRate?: number,
  type: TtdRateType,
  lastUpdated: string
}

export class SegmentModel implements Segment {
  public id!: number;
  public name!: string;
  public description!: string;
  public partnerId!: number;
  public clientSideId!: string;
  public reach!: SegmentReach;
  public platformReach?: SegmentPlatformReach;
  public sharedAccounts!: number[];
  public mainType!: string | null;
  public subType!: string | null;
  public audienceType!: string | null;
  public category!: string;
  public freeText!: string;
  public keywords!: string | null;
  public hasChildren!: boolean;
  public children!: Segment[];
  public state!: SegmentState;
  public parent?: number;
  public taxonomy?: number;
  public entityType: 'TAXONOMY' | 'SEGMENT' = 'SEGMENT';

  constructor(fields: Segment) {
    if (fields) {
      Object.assign(this, fields);
    }
  }

  static fromList(segmentsObjects: any[] = []): SegmentModel[] {
    return segmentsObjects.map((item) => {
      return SegmentModel.from(item);
    });
  }

  static from(segmentObject: Segment): SegmentModel {
    return new SegmentModel(segmentObject);
  }
}
